import type {
  GitHubInstallation,
  GitHubIssuesPermission,
  InstallationHealth
} from "@/api/useIntegrations.types"

const HEALTH_SEVERITY: Record<InstallationHealth["status"], number> = {
  healthy: 1,
  unknown: 2,
  unhealthy: 3
}

export const isUnhealthy = (
  health?: InstallationHealth | null
): health is InstallationHealth => !!health && health.status !== "healthy"

export const getWorstHealth = (
  healths: (InstallationHealth | null | undefined)[]
) =>
  healths.reduce<InstallationHealth | null>((worst, health) => {
    if (!health) return worst
    if (!worst) return health

    return HEALTH_SEVERITY[health.status] > HEALTH_SEVERITY[worst.status]
      ? health
      : worst
  }, null)

// For health read from untyped data, like the ticketing vendors' configuration
export const toInstallationHealth = (
  value: unknown
): InstallationHealth | undefined => {
  if (typeof value !== "object" || value === null) return undefined
  const { status } = value as Partial<InstallationHealth>

  return status && status in HEALTH_SEVERITY
    ? (value as InstallationHealth)
    : undefined
}

// An unknown permission (UNSPECIFIED) doesn't block issues: the lookup may have failed
const MISSING_ISSUES_PERMISSIONS: GitHubIssuesPermission[] = [
  "PENDING_APPROVAL",
  "NOT_REQUESTED"
]

export const isGitHubIssuesPermissionMissing = (
  permission: GitHubIssuesPermission
) => MISSING_ISSUES_PERMISSIONS.includes(permission)

export const allowsGitHubIssues = ({
  issuesEnabled,
  issuesPermission
}: Pick<GitHubInstallation, "issuesEnabled" | "issuesPermission">) =>
  issuesEnabled && !isGitHubIssuesPermissionMissing(issuesPermission)
